<template>
	<div
		aria-label="Deployment controls"
		class="deployment-controls"
		role="button"
		tabindex="0"
		:class="stateClasses"
		@blur="closeTooltip()"
		@click="handleClick"
		@focus="openTooltip()"
		@keydown.enter.prevent="handleClick"
		@keydown.space.prevent="handleClick"
		@mouseenter="openTooltip()"
		@mouseleave="closeTooltip()">
		<FontAwesomeIcon :icon="iconClass" />
		<Transition name="deployment-controls-tooltip">
			<div
				v-if="tooltipOpen"
				class="deployment-controls__tooltip"
				role="tooltip">
				<div class="deployment-controls__tooltip-title">{{ tooltip.title }}</div>
				<div class="deployment-controls__tooltip-body">{{ tooltip.body }}</div>
			</div>
		</Transition>
	</div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onUnmounted, ref, watch } from 'vue'
import type { Build } from '@/models/Build/Model'
import { useDeploymentListState } from '@/models/Deployment/States'
import DeploymentsApi from '@/models/Deployment/Api'
import * as Sentry from '@sentry/browser'
import { usePostHog } from '@/composables/usePostHog'
import FontAwesomeIcon from '@/components/FontAwesomeIcon.vue'

const baseUrl = import.meta.env.VITE_DEPLOYMENT_DOMAIN

const emit = defineEmits<{
	(e: 'build-requested'): void
	(e: 'view-requested'): void
	(e: 'deployed'): void
}>()

const props = defineProps<{
	build?: Build | null
	building?: boolean
}>()

const tooltipOpen = ref(false)
let tooltipTimer: any = null
const { posthog, buildUrl } = usePostHog()
const appWasReached = ref(false)
const deploymentsApi = new DeploymentsApi()
const componentUnmounted = ref(false)

const listState = useDeploymentListState()
listState.query().where('build_id', '=', 0).save()
listState.defaultParams.per_page = 1
listState.defaultParams.orderBy = 'created_at'
listState.defaultParams.order = 'desc'

type ControlState = 'ready' | 'building' | 'starting' | 'deployed' | 'failure' | 'loading'

const viewUrl = computed(() => {
	return buildUrl(`http://user-${deployment.value?.creator_id}.${baseUrl}`).value
})

const deployment = computed(() => {
	return listState?.list.value[0]
})

const appIsRunning = computed(() => {
	if (deployment.value?.status !== 'success') return false
	return appWasReached.value
})

const state = computed<ControlState>(() => {
	if (props.building || !listState.isLoaded.value || !props.build) return 'loading'
	else if (
		!deployment.value ||
		(!deployment.value?.active && deployment.value?.status !== 'pending')
	)
		return 'ready'
	else if (deployment.value?.status === 'pending') return 'building'
	else if (
		deployment.value?.status === 'deployed' ||
		(deployment.value?.status === 'success' && !appIsRunning.value)
	)
		return 'starting'
	else if (deployment.value?.status === 'success') return 'deployed'
	else if (deployment.value?.status === 'failure') return 'failure'
	else return 'loading'
})

const stateClasses = computed(() => ({
	'deployment-controls--yellow': state.value === 'ready' || state.value === 'deployed',
	'deployment-controls--red': state.value === 'failure',
	'deployment-controls--spin':
		state.value === 'building' || state.value === 'starting' || state.value === 'loading',
	'deployment-controls--pulse': state.value === 'deployed' || state.value === 'ready',
	'deployment-controls--shake': state.value === 'ready',
}))

const iconClass = computed(() => {
	switch (state.value) {
		case 'ready':
			return 'fal fa-rocket'
		case 'building':
			return 'fal fa-gear'
		case 'starting':
			return 'fal fa-gear'
		case 'failure':
			return 'fas fa-triangle-exclamation'
		case 'deployed':
			return 'fal fa-play'
		default:
			return 'fal fa-spinner-third'
	}
})

const tooltip = computed(() => {
	switch (state.value) {
		case 'ready':
			return {
				title: 'Click to build',
				body: 'Ready to build and deploy live preview',
			}
		case 'building':
			return { title: 'Preparing live preview', body: 'App is being deployed' }
		case 'starting':
			return { title: 'Preparing live preview', body: 'App is starting' }
		case 'failure':
			return {
				title: `Something's wrong`,
				body: 'We have been notified and will investigate',
			}
		case 'deployed':
			return { title: 'Live preview ready', body: 'Click to open app' }
		default:
			return { title: 'Loading', body: 'Give us just a sec...' }
	}
})

useInterval()

function handleClick() {
	if (state.value === 'deployed' && viewUrl) {
		posthog.capture('application_viewed')
		openViewUrl()
	} else if (state.value !== 'building') {
		emit('build-requested')
	}
}

function openTooltip() {
	clearTimeout(tooltipTimer)
	tooltipTimer = setTimeout(() => {
		tooltipOpen.value = true
	}, 120)
}

function closeTooltip() {
	clearTimeout(tooltipTimer)
	tooltipTimer = setTimeout(() => {
		tooltipOpen.value = false
	}, 120)
}

function openViewUrl() {
	const creatorId = deployment.value?.creator_id
	if (!creatorId) return
	window.open(viewUrl.value, '_blank')
}

function useInterval() {
	let intervalTries = 0
	let statusInterval: any = null

	const intervalShouldBeRunning = computed(
		() =>
			!!deployment.value &&
			deployment.value.status !== 'success' &&
			deployment.value.status !== 'failure',
	)

	const deploymentFinished = computed(() => {
		return deployment.value?.status === 'success' || deployment.value?.status === 'failure'
	})

	watch(
		() => props.build,
		(newBuild, oldBuild) => {
			if (!newBuild) return

			listState.query().where('build_id', '=', newBuild.id).save()

			// If a build is built or a new build is created we should refresh the deployment status
			if (!newBuild.draft || newBuild.id !== oldBuild?.id) {
				refresh()
			}
		},
		{ deep: true, immediate: true },
	)

	watch(
		() => deploymentFinished.value,
		() => {
			if (!deploymentFinished.value) return
			pingApp()
		},
		{ immediate: true },
	)

	watch(
		() => props.building,
		() => {
			if (props.building) return
			refresh()
		},
		{ deep: true },
	)

	onBeforeMount(() => {
		refresh()
	})

	onUnmounted(() => {
		clearInterval(statusInterval)
		componentUnmounted.value = true
	})

	function setStatusInterval() {
		// If interval should not be running, clear it
		if (!intervalShouldBeRunning.value) {
			if (statusInterval) clearInterval(statusInterval)
			intervalTries = 0
			statusInterval = null
			return
		}
		// If interval is already running, return
		if (statusInterval) {
			return
		}
		// Start interval
		statusInterval = setInterval(async () => {
			intervalTries++
			if (intervalTries === 48) {
				Sentry.captureMessage('App not deployed after 4 minutes')
			}
			// If interval should not be running, clear it
			if (!intervalShouldBeRunning.value) {
				clearInterval(statusInterval)
				intervalTries = 0
				statusInterval = null
				return
			}
			await refresh()
			if (deploymentFinished.value) {
				emit('deployed')
			}
		}, 2000)
	}

	async function refresh() {
		// If the deployment for the build is already loaded and finished we don't
		// need to fetch it again
		await listState.getList()
		setStatusInterval()
	}

	async function pingApp() {
		if (!props.build) return
		if (componentUnmounted.value) return
		if (!deploymentFinished.value) {
			appWasReached.value = false
			return
		}

		try {
			const response = await deploymentsApi.pingApp({ user_id: props.build.creator_id })
			if (response.data.running && response.data.buildId === String(props.build.id)) {
				appWasReached.value = true
			} else {
				appWasReached.value = false
				setTimeout(pingApp, 1000)
			}
		} catch (error) {
			appWasReached.value = false
			setTimeout(pingApp, 1000)
		}
	}
}
</script>

<style scoped lang="scss">
.deployment-controls {
	position: fixed;
	right: 34px;
	bottom: 24px;
	z-index: 2000;
	width: 64px;
	height: 64px;
	border-radius: 999px;
	display: grid;
	place-content: center;
	box-shadow:
		0 12px 28px rgba(0, 0, 0, 0.25),
		0 2px 8px rgba(0, 0, 0, 0.15) inset;
	cursor: pointer;
	user-select: none;
	transition:
		right 100ms ease,
		bottom 100ms ease,
		width 100ms ease,
		height 100ms ease,
		box-shadow 200ms ease;
	border: 2px solid rgba(255, 255, 255, 0.8);

	/* Icon color default to white for contrast */
	color: #ffffff;

	&:hover {
		width: 66px;
		height: 66px;
		right: 33px;
		bottom: 23px;

		&::before {
			inset: -4px;
		}

		.deployment-controls__tooltip {
			right: 77px;
			bottom: -7px;
		}
	}

	/* colors */
	&--yellow {
		background: var(--p-yellow-500);
		color: #000000;
	}
	&--red {
		background: var(--p-red-500);
	}

	/* animated ring */
	&::before {
		content: '';
		position: absolute;
		inset: -6px;
		border-radius: inherit;
		border: 2px solid rgba(255, 255, 255, 0.35);
		filter: blur(0.2px);
		pointer-events: none;
		transition: inset 100ms ease;
	}

	/* animations */
	&--spin :deep(svg) {
		animation: spin 1.1s linear infinite;
	}
	&--shake :deep(svg) {
		animation: shake 3s ease-in-out infinite;
	}
	&--pulse::before {
		animation: pulse 2.2s ease-out infinite;
	}

	.deployment-controls__icon {
		font-size: 26px;

		&.fa-play {
			margin-right: -3px;
		}

		&.fa-rocket {
			margin-left: -2px;
		}

		&.fa-triangle-exclamation {
			margin-top: -4px;
		}
	}

	.deployment-controls__tooltip {
		position: absolute;
		bottom: -8px;
		right: 76px;
		width: 280px;
		padding: 14px 14px 12px 14px;
		border-radius: 14px;
		background: rgba(17, 17, 17, 0.9);
		backdrop-filter: blur(6px);
		border: 1px solid rgba(255, 255, 255, 0.12);
		box-shadow: 0 16px 48px rgba(0, 0, 0, 0.35);
		color: #fff;
		transition:
			right 100ms ease,
			bottom 100ms ease;

		.deployment-controls__tooltip-title {
			font-weight: 800;
			margin-bottom: 6px;
			font-size: 16px;
			line-height: 1.1;
		}
		.deployment-controls__tooltip-body {
			font-size: 13px;
			opacity: 0.9;
			margin-bottom: 10px;
		}
	}

	.deployment-controls-tooltip-enter-from,
	.deployment-controls-tooltip-leave-to {
		opacity: 0;
		transform: translateY(6px);
	}
	.deployment-controls-tooltip-enter-active,
	.deployment-controls-tooltip-leave-active {
		transition:
			opacity 140ms ease,
			transform 140ms ease;
	}
	.deployment-controls-tooltip-enter-to,
	.deployment-controls-tooltip-leave-from {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes spin {
	from {
		transform: rotate(0deg);
	}
	to {
		transform: rotate(360deg);
	}
}
@keyframes shake {
	0%,
	100% {
		transform: rotate(0deg);
	}
	2% {
		transform: rotate(-6deg);
	}
	4% {
		transform: rotate(6deg);
	}
	6% {
		transform: rotate(-3deg);
	}
	8% {
		transform: rotate(2deg);
	}
	10% {
		transform: rotate(0deg);
	}
}
@keyframes pulse {
	0% {
		box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.25);
	}
	70% {
		box-shadow: 0 0 0 12px rgba(255, 255, 255, 0);
	}
	100% {
		box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
	}
}
</style>
